import { Box, GKButton, Typography } from "components";
import { moneyCommaSeparator } from "utils";
import { PlaceResultItem } from "./useMFConsent";

interface Props {
  items: PlaceResultItem[];
}

const toNumber = (v: string | null | undefined): number | null => {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

// The backend signals failure via `errorCode`: a non-empty code (e.g. "400")
// means the order failed; an empty code means it was placed successfully.
const isFailedItem = (item: PlaceResultItem): boolean =>
  !!(item.errorCode ?? "").trim();

const OrderResultTable = ({ items }: Props) => (
  <Box
    sx={{
      border: "1px solid var(--neutral-100)",
      borderRadius: "12px",
      overflow: "hidden",
    }}
  >
    {/* header */}
    <Box
      sx={{
        bgcolor: "var(--grey-bg-100)",
        borderBottom: "1px solid var(--neutral-100)",
        display: { xs: "none", sm: "grid" },
        gap: "12px",
        gridTemplateColumns: "2fr 1.2fr 1fr 1.4fr",
        px: "16px",
        py: "12px",
      }}
    >
      {["Scheme", "Folio No.", "Amount", "Status"].map((h, i) => (
        <Typography
          key={h}
          color="var(--neutral-500)"
          fontSize={11}
          fontWeight={600}
          sx={{
            letterSpacing: "0.3px",
            textAlign: i === 2 ? "right" : "left",
            textTransform: "uppercase",
          }}
        >
          {h}
        </Typography>
      ))}
    </Box>

    {items.map((item) => {
      const isSuccess = !isFailedItem(item);
      const amount = toNumber(item.amount);
      const hasPaymentLink = isSuccess && !!item.paymentTokenUrl;
      return (
        <Box
          key={item.id}
          sx={{
            alignItems: { xs: "flex-start", sm: "center" },
            borderBottom: "1px solid var(--neutral-100)",
            display: "grid",
            gap: "12px",
            gridTemplateColumns: { xs: "1fr", sm: "2fr 1.2fr 1fr 1.4fr" },
            px: "16px",
            py: "14px",
            "&:last-of-type": { borderBottom: 0 },
          }}
        >
          <Typography color="var(--black-900)" fontSize={13} fontWeight={600}>
            {item.scheme?.name ?? "-"}
          </Typography>

          <Typography
            color="var(--neutral-700)"
            fontSize={13}
            fontWeight={500}
            sx={{ fontStyle: item.folioNumber ? "normal" : "italic" }}
          >
            {item.folioNumber ?? "New Folio"}
          </Typography>

          <Typography
            color="var(--black-900)"
            fontSize={13}
            fontWeight={600}
            sx={{ textAlign: { xs: "left", sm: "right" } }}
          >
            {amount != null ? `₹${moneyCommaSeparator(amount)}` : "-"}
          </Typography>

          <Box>
            {hasPaymentLink ? (
              <GKButton
                variant="contained"
                size="small"
                onClick={() =>
                  window.open(
                    item.paymentTokenUrl as string,
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
              >
                Pay Now
              </GKButton>
            ) : isSuccess ? (
              <Typography
                color="var(--success-700, #15803d)"
                fontSize={13}
                fontWeight={600}
              >
                Placed
              </Typography>
            ) : (
              <Typography
                color="var(--error-700, #c35245)"
                fontSize={12}
                fontWeight={500}
                sx={{ lineHeight: 1.4 }}
              >
                {item.errorMessage || "Order failed"}
              </Typography>
            )}
          </Box>
        </Box>
      );
    })}
  </Box>
);

export default OrderResultTable;
